import {
	Alert,
	AlertDescription,
	Box,
	Button,
	Grid,
	Heading,
	HStack,
	Input,
	InputGroup,
	InputRightAddon,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	SimpleGrid,
	Skeleton,
	SkeletonText,
	Stack,
	Text,
	useDisclosure,
	VStack,
} from "@chakra-ui/react";
import apiFetch from "@wordpress/api-fetch";
import { sprintf, __ } from "@wordpress/i18n";
import React from "react";
import { useMutation } from "react-query";
import { Search } from "../../../base/components/Icons";
import { ArrowsRepeat } from "../../../dashboard/components/Icon";
import { withQueryClientProvider } from "../../hoc";
import { ScrollArea } from "../scroll-area/ScrollArea";
import LibraryItem from "./LibraryItem";
import LibraryModalTrigger from "./LibraryModalTrigger";
import { catBtnStyles, typeBtnStyles } from "./styles";
import "./styles.scss";
import useLibrary from "./useLibrary";

type Props = Pick<ReturnType<typeof useDisclosure>, "isOpen" | "onClose">;

const LibraryModal = withQueryClientProvider(({ isOpen, onClose }: Props) => {
	const {
		reset,
		templates,
		contentType,
		selectedCategory,
		searchQuery,
		selectedStarterPack,
		setSelectedStarterPack,
		setSearchQuery,
		setContentType,
		setSelectedCategory,
		categorizedData,
		libraryDataQuery,
		currentTemplates,
	} = useLibrary();

	const libraryMutation = useMutation(() => {
		return apiFetch({
			path: "magazine-blocks/v1/library-data?refresh=true",
		});
	});

	return (
		<Modal isOpen={isOpen} onClose={onClose} isCentered motionPreset="none">
			<ModalOverlay />
			<ModalContent
				borderRadius="5px"
				width="100%"
				maxWidth="100%"
				boxShadow="none"
				height="100%"
				margin="0"
			>
				<ModalHeader
					minH="60px"
					p="0"
					display="flex"
					alignItems="center"
					borderBottom="1px"
					borderColor="gray.200"
				>
					<Box w="22%" flexShrink={0} h="full" px="4" py="3">
						<svg
							width="37"
							height="36"
							viewBox="0 0 37 36"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<rect width="24" height="24" fill="white"></rect>
							<path
								d="M12 18.7957H4.60217V5.20432L12 9.82797V18.7957Z"
								fill="#690AA0"
							></path>
							<path
								d="M19.4194 18.7957H12V9.82797L19.4194 5.20432V18.7957Z"
								fill="#8D42CE"
							></path>
							<path
								d="M24 24H0V0H24V24ZM1.07527 22.9247H22.9247V1.07527H1.07527V22.9247Z"
								fill="#690AA0"
							></path>
						</svg>
					</Box>
					<HStack h="full">
						<Button
							isActive={"sections" === contentType}
							onClick={() => {
								setContentType("sections");
								reset();
								libraryDataQuery.refetch();
							}}
							{...typeBtnStyles}
						>
							Sections
						</Button>
						<Button
							isActive={"starter-packs" === contentType}
							onClick={() => {
								setContentType("starter-packs");
								reset();
								libraryDataQuery.refetch();
							}}
							{...typeBtnStyles}
						>
							Starter Packs
						</Button>
						<Button
							isActive={"templates" === contentType}
							onClick={() => {
								setContentType("templates");
								reset();
								libraryDataQuery.refetch();
							}}
							{...typeBtnStyles}
						>
							Templates
						</Button>
						<Button
							isActive={"favorites" === contentType}
							onClick={() => {
								setContentType("favorites");
								reset();
								libraryDataQuery.refetch();
							}}
							display="flex"
							alignItems="center"
							leftIcon={
								<svg
									stroke="currentColor"
									fill="none"
									strokeWidth="2"
									viewBox="0 0 24 24"
									strokeLinecap="round"
									strokeLinejoin="round"
									height="14px"
									width="14px"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
								</svg>
							}
							{...typeBtnStyles}
						>
							Favorites
						</Button>
					</HStack>
				</ModalHeader>
				<Button
					onClick={() => {
						libraryMutation.mutate();
					}}
					padding="0"
					bgColor="transparent"
					position="absolute"
					top="14px"
					right="45px"
					width="18px"
					height="18px"
					borderRight="1px"
					borderColor="gray.200"
					borderStyle="solid"
					_hover={{ bgColor: "transparent" }}
				>
					<ArrowsRepeat height="4" width="4" />
				</Button>
				<ModalCloseButton />
				<ModalBody p="0" h="calc(100% - 60px)">
					<HStack h="full">
						<Box
							w="22%"
							borderRight="1px"
							borderColor="gray.200"
							p="20px"
							flexShrink={0}
							h="full"
						>
							<InputGroup>
								<Input
									onChange={(e) =>
										setSearchQuery(e.target.value)
									}
									value={searchQuery}
									borderRight="0"
									borderTopLeftRadius="sm"
									borderBottomLeftRadius="sm"
								/>
								<InputRightAddon
									bg="white"
									borderTopRightRadius="sm"
									borderBottomRightRadius="sm"
								>
									<Search h="18px" w="18px" fill="gray.400" />
								</InputRightAddon>
							</InputGroup>
							<Heading
								as="h3"
								fontSize="13px"
								fontWeight="semibold"
								m="0"
								mt="8 !important"
								mb="4 !important"
							>
								Categories
							</Heading>
							<ScrollArea
								h="calc(100% - 110px)"
								w="calc(100% + 20px)"
								pr="4"
							>
								<VStack alignItems="start" gap="0">
									<Button
										{...catBtnStyles}
										isActive={!selectedCategory}
										onClick={() => setSelectedCategory("")}
									>
										{sprintf(
											__("All %s", "magazine-blocks"),
											contentType === "sections"
												? __(
														"Sections",
														"magazine-blocks"
												  )
												: contentType ===
												  "starter-packs"
												? __(
														"Starter Packs",
														"magazine-blocks"
												  )
												: contentType === "favorites"
												? __(
														"Favorites",
														"magazine-blocks"
												  )
												: __(
														"Templates",
														"magazine-blocks"
												  )
										)}
										<Text as="span">
											{libraryMutation.isLoading
												? 0
												: templates.length}
										</Text>
									</Button>
									{libraryMutation.isLoading ? null : (
										<>
											{contentType !== "favorites"
												? Object.values(
														categorizedData
												  ).map((s) => (
														<Button
															isActive={
																s?.slug ===
																selectedCategory
															}
															key={s.slug}
															{...catBtnStyles}
															onClick={() =>
																setSelectedCategory(
																	s.slug
																)
															}
														>
															{s?.name}
															<Text as="span">
																{s?.count}
															</Text>
														</Button>
												  ))
												: null}
										</>
									)}
								</VStack>
							</ScrollArea>
						</Box>
						<Box h="full" w="full" p="8">
							<HStack w="full" mb="32px !important">
								{contentType === "starter-packs" &&
								selectedStarterPack ? (
									<Button
										variant="link"
										onClick={() =>
											setSelectedStarterPack(undefined)
										}
										size="xs"
									>
										{__("Go back", "magazine-blocks")}
									</Button>
								) : (
									<Text as="span" fontWeight="600">
										{contentType === "sections"
											? __("Sections", "magazine-blocks")
											: contentType === "starter-packs"
											? __(
													"Starter Packs",
													"magazine-blocks"
											  )
											: contentType === "favorites"
											? __("Favorites", "magazine-blocks")
											: __(
													"Templates",
													"magazine-blocks"
											  )}
									</Text>
								)}
							</HStack>
							<ScrollArea h="calc(100% - 25px)" mr="-32px">
								{!libraryDataQuery.isLoading &&
								!libraryMutation.isLoading &&
								libraryDataQuery.isSuccess ? (
									currentTemplates?.length ? (
										<SimpleGrid
											h="full"
											columns={[1, 2, 3]}
											spacing="27px"
											gridAutoRows="min-content"
											pr="8"
											alignItems="start"
											mr="-32px"
										>
											{currentTemplates.map((t) => {
												if (
													selectedCategory &&
													!t.category.some(
														(c) =>
															c.slug ===
															selectedCategory
													)
												) {
													return null;
												}
												return (
													<LibraryItem
														id={t?.ID}
														imageUrl={
															t?.post_thumbnail
														}
														key={t?.ID}
														onClose={onClose}
														isStarter={
															!selectedStarterPack &&
															"starter-packs" ===
																contentType
														}
														onClick={
															"starter-packs" ===
																contentType &&
															!selectedStarterPack
																? () => {
																		setSelectedStarterPack(
																			t.ID
																		);
																  }
																: undefined
														}
														title={t?.post_title}
														slug={t?.slug}
														type={contentType}
													/>
												);
											})}
										</SimpleGrid>
									) : !searchQuery &&
									  contentType === "favorites" ? (
										<Box>
											<VStack
												w="full"
												h="400px"
												borderRadius="4px"
												display="flex"
												justifyContent="center"
												alignItems="center"
											>
												<svg
													xmlns="http://www.w3.org/2000/svg"
													fill="none"
													viewBox="0 0 81 80"
													width="80px"
													height="80px"
												>
													<circle
														cx="40.5"
														cy="40"
														r="40"
														fill="#F4F4F4"
													/>
													<path
														fill="#D3D3D3"
														d="M57.143 37.61C55.32 47.337 40.75 55.746 40.75 55.746s-14.57-8.41-16.393-18.138c-1.796-9.586 3.488-14.592 8.949-14.592a8.038 8.038 0 0 1 4.224.886 8.37 8.37 0 0 1 3.22 2.965 8.37 8.37 0 0 1 3.22-2.965 8.038 8.038 0 0 1 4.224-.886c5.461 0 10.745 5.006 8.949 14.592Z"
													/>
												</svg>
												<Box>
													<Text
														fontSize="20px"
														fontWeight="600"
														mt="16px"
														textAlign="center"
														color="#383838"
													>
														{__(
															"Collect your Favourites",
															"magazine-blocks"
														)}
													</Text>
												</Box>
											</VStack>
										</Box>
									) : (
										<Box>
											<VStack
												w="full"
												h="400px"
												borderRadius="4px"
												display="flex"
												justifyContent="center"
												alignItems="center"
											>
												<svg
													xmlns="http://www.w3.org/2000/svg"
													fill="none"
													viewBox="0 0 81 80"
													width="80px"
													height="80px"
												>
													<g
														fill="#D3D3D3"
														clipPath="url(#a)"
													>
														<path d="M78.19 77.713a4.997 4.997 0 0 1-7.063 0l-16.025-16.03 7.062-7.063L78.19 70.67a4.996 4.996 0 0 1 0 7.043Z" />
														<path d="M10.645 10.17a34.678 34.678 0 1 0 49.067 49.017 34.678 34.678 0 0 0-49.068-49.018Zm47.064 47.064a31.86 31.86 0 1 1-45.056-45.057A31.86 31.86 0 0 1 57.71 57.234Z" />
													</g>
													<defs>
														<clipPath id="a">
															<path
																fill="#fff"
																d="M.5 0h80v80H.5z"
															/>
														</clipPath>
													</defs>
												</svg>
												<Box>
													<Text
														fontSize="20px"
														fontWeight="600"
														mt="16px"
														textAlign="center"
														color="#383838"
													>
														{__(
															"Sorry, no result found",
															"magazine-blocks"
														)}
													</Text>
													<Text
														fontSize="14px"
														fontWeight="600"
														mt="4px"
														textAlign="center"
														color="#7A7A7A"
													>
														{__(
															"Please try another search",
															"magazine-blocks"
														)}
													</Text>
												</Box>
											</VStack>
										</Box>
									)
								) : libraryDataQuery.isLoading ||
								  libraryMutation.isLoading ? (
									<Grid
										gridTemplateColumns="repeat(auto-fill, minmax(290px, 1fr))"
										gridGap="27px"
										pr="8"
									>
										{Array.from({ length: 20 }).map(
											(_, i) => (
												<Stack
													key={i}
													p="12px"
													borderRadius="4px"
													border="1px solid"
													borderColor="gray.100"
													bgColor="white"
												>
													<Skeleton
														w="full"
														h="200px"
													/>
													<HStack justify="space-between">
														<SkeletonText
															noOfLines={1}
															width="80px"
														/>
														<Skeleton
															h="14px"
															w="14px"
															m="14px"
															borderRadius="full"
														/>
													</HStack>
												</Stack>
											)
										)}
									</Grid>
								) : libraryDataQuery.isError ? (
									<Alert status="error">
										<AlertDescription>
											{(libraryDataQuery.error as Error)
												?.message ??
												__(
													"Something went wrong",
													"magazine-blocks"
												)}
										</AlertDescription>
									</Alert>
								) : null}
							</ScrollArea>
						</Box>
					</HStack>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
});

export default withQueryClientProvider(LibraryModal);
export { LibraryModalTrigger };
